import { useCallback, useState } from 'react';
import JSZip from 'jszip';
import type { OpaqueVideoData } from '@lumen5/luminary';
import { OfflineAudioEncoder, VideoData } from '@lumen5/luminary';

import { INFINITE_TOAST_DURATION, Toast } from 'js/src/components/common/Toast';
import type { GifConversionResult } from 'js/src/components/common/video-downloads/mp4ToGif';
import { FREE_PLAN_NAME } from 'js/src/libs/consts';
import { downloadBlob } from 'js/src/libs/download-blob';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import { withDataLuminary } from 'js/src/luminary/data-luminary';

const PREVIEW_RESOLUTION_VALUE = 512;
const FULL_RESOLUTION_VALUE = 1080;

type VideoArtifact = {
    videoData: OpaqueVideoData;
    videoName: string;
};

/** GIF works off the rendered mp4, not the video data, so it takes a url instead. */
type GifArtifact = {
    videoUrl: string;
    videoName: string;
};

/**
 * Renders one PNG per scene and packs them into a zip.
 *
 * `fullResolution` selects between the two variants we offer:
 *  - false: a 512px preview render off whatever media is already available.
 *  - true: a 1080p render (Luminary's ceiling) off each asset's `renderUrl`. Those urls are only
 *    injected by the render pipeline, so this variant requires a published video -- see
 *    `canDownloadFullResolution` below.
 */
function useSceneImagesZipDownload({ fullResolution }: { fullResolution: boolean }) {
    const [downloading, setDownloading] = useState(false);

    const download = useCallback(
        async ({ videoData, videoName }: VideoArtifact) => {
            setDownloading(true);
            try {
                const zip = new JSZip();

                await withDataLuminary(
                    async (luminary) => {
                        // Untimed on purpose: an export is user-initiated and may legitimately
                        // load for minutes.
                        luminary.load(videoData);
                        await luminary.onAssetsDoneTryingToLoad();
                        await luminary.onCanPlay();
                        await luminary.onMatchCutsRehydrated();

                        let sceneIndex = 0;
                        for (const scene of luminary.scenes) {
                            const canvas = document.createElement('canvas');
                            const { width, height } = luminary.outputResolution;
                            canvas.width = width;
                            canvas.height = height;

                            try {
                                await scene.renderPreviewToCanvas(canvas, { subsceneIndex: 0 });
                            } catch (e) {
                                Toast.error(`Failed to render scene ${sceneIndex + 1}`);
                                continue;
                            }

                            const blob: Blob = await new Promise((resolve, reject) => {
                                canvas.toBlob((blobResult) => {
                                    if (!blobResult) {
                                        reject(
                                            new Error(
                                                `Failed to create blob for scene ${scene.id}`,
                                            ),
                                        );
                                    } else {
                                        resolve(blobResult);
                                    }
                                }, 'image/png');
                            });
                            zip.file(`scene_${++sceneIndex}.png`, blob);
                        }
                    },
                    {
                        luminarySettings: {
                            fetchResources: {
                                audio: false,
                                media: true,
                                transitions: false,
                                fonts: true,
                            },
                            disableRenderer: false,
                            resolutionValue: fullResolution
                                ? FULL_RESOLUTION_VALUE
                                : PREVIEW_RESOLUTION_VALUE,
                            ...(fullResolution
                                ? { mediaSizeToUse: VideoData.RENDER_MEDIA_SIZE }
                                : {}),
                        },
                    },
                );

                const content = await zip.generateAsync({ type: 'blob' });
                downloadBlob(content, `${videoName}_${fullResolution ? 'carousel' : 'scenes'}.zip`);
                analytics.track('ZIP Previews Button Clicked', { full_resolution: fullResolution });
            } catch (err) {
                Toast.error('Download failed.');
                ErrorReporter.capture(err);
                analytics.track('ZIP Previews Button Failed', {
                    full_resolution: fullResolution,
                });
            } finally {
                setDownloading(false);
            }
        },
        [fullResolution],
    );

    return { download, downloading };
}

export function useScenePreviewsZipDownload() {
    return useSceneImagesZipDownload({ fullResolution: false });
}

/**
 * Full-resolution scene stills ("carousel images"). Requires a published video: `RENDER_MEDIA_SIZE`
 * resolves to each asset's `renderUrl`, which only the render pipeline injects, and MediaAsset
 * throws outright when it is missing -- so callers must gate this on the video being PUBLISHED.
 */
export function useCarouselImagesZipDownload() {
    return useSceneImagesZipDownload({ fullResolution: true });
}

/**
 * Carousel is a paid feature, available from Basic up. There is no carousel-specific capability on
 * the backend, and the render_* capabilities don't line up with that boundary (render_1080p is off
 * on Basic, render_720p is on even for Free), so this keys off the plan directly.
 */
export function canDownloadCarouselImages(): boolean {
    return SERVER.ANALYTICS?.plan_name !== FREE_PLAN_NAME;
}

/**
 * GIF export is a paid feature on the same footing as carousel -- Basic and up. It keys off the
 * plan directly for the same reason: there is no backend capability that draws this line.
 */
export function canDownloadGif(): boolean {
    return SERVER.ANALYTICS?.plan_name !== FREE_PLAN_NAME;
}

/**
 * Converts the already-rendered mp4 into an animated GIF in the browser.
 *
 * Unlike the other artifacts this works off the rendered video rather than the video data, so it
 * needs a published video and its download url. The encoder and demuxer are heavy and only loaded
 * when someone actually converts, so `convertMp4ToGif` is imported lazily here too.
 */
export function useGifDownload() {
    const [downloading, setDownloading] = useState(false);
    const [progress, setProgress] = useState(0);

    const download = useCallback(async ({ videoUrl, videoName }: GifArtifact) => {
        setDownloading(true);
        setProgress(0);
        // Conversion takes ~10s for a minute of video, and the only other signal is a spinner on
        // the tile -- easy to miss, and the panel may well be closed by then. Hold a toast open for
        // the duration and replace it in place when the download starts or the conversion fails.
        const toastId = Toast.loading(
            'Generating GIF...',
            undefined,
            undefined,
            INFINITE_TOAST_DURATION,
        );
        let result: GifConversionResult | null = null;
        let shownPercent = 0;
        try {
            const { convertMp4ToGif } =
                await import('js/src/components/common/video-downloads/mp4ToGif');
            result = await convertMp4ToGif({
                videoUrl,
                onProgress: ({ ratio }) => {
                    setProgress(ratio);
                    // Fires once per encoded frame (~600 times for a minute of video), so only
                    // touch the toast when the number a user can actually read has changed.
                    const percent = Math.round(ratio * 100);
                    if (percent === shownPercent) {
                        return;
                    }
                    shownPercent = percent;
                    Toast.loading(
                        `Generating GIF... ${percent}%`,
                        undefined,
                        toastId,
                        INFINITE_TOAST_DURATION,
                    );
                },
            });
            downloadBlob(result.blob, `${videoName}.gif`);
            Toast.success('GIF download started!', undefined, toastId);
            analytics.track('GIF Download Button Clicked', {
                duration_millis: Math.round(result.durationMillis),
                frame_count: result.frameCount,
                size_bytes: result.blob.size,
            });
        } catch (err) {
            Toast.error('GIF conversion failed.', undefined, toastId);
            ErrorReporter.capture(err);
            analytics.track('GIF Download Button Failed');
        } finally {
            setDownloading(false);
            setProgress(0);
        }
    }, []);

    return { download, downloading, progress };
}

/** Encodes the video's full mixed audio track (music + voiceover) to a .wav. */
export function useAudioWavDownload() {
    const [downloading, setDownloading] = useState(false);

    const download = useCallback(async ({ videoData, videoName }: VideoArtifact) => {
        setDownloading(true);
        try {
            const duration = await withDataLuminary(
                async (dataLuminary) => {
                    // Untimed on purpose: a download is user-initiated and may legitimately load
                    // for minutes.
                    dataLuminary.load(videoData);
                    await dataLuminary.onCanPlay();
                    return dataLuminary.duration;
                },
                {
                    luminarySettings: {
                        fetchResources: {
                            audio: true,
                            media: false,
                            transitions: false,
                            fonts: false,
                        },
                    },
                },
            );

            if (!duration || duration <= 0) {
                throw new Error('Video has no audio or invalid duration');
            }

            const encoder = new OfflineAudioEncoder();
            encoder.setBasename(`${videoName}_audio`);
            encoder.setStartTime(0);
            encoder.setStopTime(duration);

            encoder.luminary.load(videoData);
            await encoder.encode();
            encoder.download();

            Toast.success('Audio download started!');
            analytics.track('Audio WAV Download Button Clicked');
        } catch (err) {
            Toast.error('Audio download failed.');
            ErrorReporter.capture(err);
            analytics.track('Audio WAV Download Button Failed');
        } finally {
            setDownloading(false);
        }
    }, []);

    return { download, downloading };
}
